import {
  createContext,
  useContext,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from "react";

type DeviceType = "mobile" | "tablet" | "desktop";
type ColorMode = "light" | "dark";

interface ThemeContextValue {
  device: DeviceType;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  colorMode: ColorMode;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

// Breakpoints (matching Tailwind defaults)
const MOBILE_BREAKPOINT = 640; // sm
const TABLET_BREAKPOINT = 1024; // lg

function getDeviceType(width: number): DeviceType {
  if (width < MOBILE_BREAKPOINT) return "mobile";
  if (width < TABLET_BREAKPOINT) return "tablet";
  return "desktop";
}

// Cached values for useSyncExternalStore
let cachedDevice: DeviceType = "desktop";
let listeners: (() => void)[] = [];

function subscribe(callback: () => void) {
  listeners.push(callback);

  const handleResize = () => {
    const newDevice = getDeviceType(window.innerWidth);
    if (newDevice !== cachedDevice) {
      cachedDevice = newDevice;
      listeners.forEach((l) => l());
    }
  };

  window.addEventListener("resize", handleResize);

  return () => {
    listeners = listeners.filter((l) => l !== callback);
    window.removeEventListener("resize", handleResize);
  };
}

function getSnapshot(): DeviceType {
  if (globalThis.window === undefined) return "desktop";
  const current = getDeviceType(window.innerWidth);
  if (current !== cachedDevice) {
    cachedDevice = current;
  }
  return cachedDevice;
}

function getServerSnapshot(): DeviceType {
  return "desktop"; // Default to desktop for SSR
}

interface ThemeProviderProps {
  readonly children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const device = useSyncExternalStore(
    subscribe,
    getSnapshot,
    getServerSnapshot,
  );

  // Placeholder for color mode - will be implemented later
  const colorMode: ColorMode = "light";

  const value = useMemo<ThemeContextValue>(
    () => ({
      device,
      isMobile: device === "mobile",
      isTablet: device === "tablet",
      isDesktop: device === "desktop",
      colorMode,
    }),
    [device, colorMode],
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
}
